/**
 * The message Ask opens with after someone marks eval results wrong.
 *
 * It carries everything Ask needs to act without asking back: the eval, the
 * results by id with what the eval said and what the person says it should
 * have been, the person's own words, the step review when there was one, and
 * what to do — read each result and its trace, decide per result who is
 * right (the person can be wrong too), find the step that decided wrongly,
 * review the design, and record a proposed change with improve_eval on
 * exactly those results.
 */

const TARGET_WORD = { run: 'run-level', span: 'span-level', conversation: 'conversation-level' };
const should = (p) => (p === true ? 'Pass' : p === false ? 'Fail' : 'the other way');

/**
 * @param {object}   o
 * @param {object}   o.lineage             { name, target, agent_name, production_version }
 * @param {object[]} o.results             [{ id, said, version, path, unit, correctedPassed, reason, fixKind?, steps? }]
 *                                         said: what the eval decided, in words; unit: "run 1b2c…"
 *                                         steps: [{ name, said, right: bool, shouldHaveBeen?, reason? }] from a step review
 *                                         fixKind: 'step' (a step was corrected) | 'eval' (every step right: a check is missing)
 * @param {string}   [o.reason]            one reason given for all of them (bulk)
 */
export function markedWrongPrompt({ lineage = {}, results = [], reason = '' }) {
  const n = results.length;
  const name = lineage.name || 'this';
  const kind = TARGET_WORD[lineage.target] || '';
  const reasons = [...new Set(results.map((r) => (r.reason || '').trim()).filter(Boolean))];
  const shared = (reason || '').trim() || (reasons.length === 1 ? reasons[0] : '');
  const allEval = n > 0 && results.every((r) => r.fixKind === 'eval');
  const anyReview = results.some((r) => Array.isArray(r.steps) && r.steps.length);

  const lines = [];
  lines.push(`I marked ${n === 1 ? 'a result' : `${n} results`} of the "${name}" eval wrong`
    + ` (${[kind ? `a ${kind} eval` : null, lineage.agent_name ? `on ${lineage.agent_name}` : null, lineage.production_version ? `v${lineage.production_version} in production` : null].filter(Boolean).join(', ')}).`);
  if (shared) lines.push('', `What I said: "${shared}"`);
  lines.push('', n === 1 ? 'The result:' : 'The results:');
  results.forEach((r, i) => {
    const own = !shared && r.reason ? `: "${r.reason.trim()}"` : '';
    const where = [r.version ? `v${r.version}` : null, r.path ? `path ${r.path}` : null].filter(Boolean).join(', ');
    lines.push(`${i + 1}. The eval said ${r.said}${where ? ` (${where})` : ''}: it should have been ${should(r.correctedPassed)}${own}.`
      + ` Result ${r.id}${r.unit ? `, ${r.unit}` : ''}.`);
    if (Array.isArray(r.steps) && r.steps.length) {
      lines.push(`   I reviewed every step${r.fixKind === 'eval' ? ' and each one was right: the eval is missing a check' : ''}:`);
      for (const s of r.steps) {
        lines.push(`   - ${s.name}: said ${s.said}; ${s.right ? 'right' : `should have been ${s.shouldHaveBeen}`}${s.reason ? ` ("${s.reason}")` : ''}.`);
      }
    }
  });
  lines.push(
    '',
    'Please:',
    `1. Read ${n === 1 ? 'it' : 'each one'} with every step's answer (eval_results with these result ids, with_steps) and the trace it scored: the input, the answer, and the tool outputs the answer rests on.`,
    `2. Decide ${n === 1 ? '' : 'per result '}who is right: the eval (the agent really went wrong) or me (the eval decided it wrongly). If you disagree with me, say why from the trace.`,
    allEval
      ? '3. Every step answered correctly and the verdict is still wrong, so a check is missing: work out what the eval should have looked at and did not, and where in the graph it belongs.'
      : anyReview
        ? '3. Start from the steps I corrected: whether the fault is that step\'s prompt or check, a threshold, the routing, or the context it reads, and review the eval\'s whole design against what it is meant to measure, not only these results.'
        : '3. Find what in the eval decided wrongly: which step, and whether it is its prompt or check, a threshold, the routing, or the context it reads, and review the eval\'s whole design against what it is meant to measure, not only these results.',
    `4. Propose the change with improve_eval on ${n === 1 ? 'this result' : 'these results'}: the smallest change that decides ${n === 1 ? 'it' : 'them'} rightly without breaking results people labelled, and what else it would flip. If the agent was at fault, say what its engineers should fix.`,
  );
  return lines.join('\n');
}

export default markedWrongPrompt;
